import Money from 'common/Money';
import * as React from 'react';
import {Link} from 'react-router-dom';
import Section from 'src/DetailComponents/Section';
import {useCurrencyLabel, useMoneyFormatter} from 'src/hooks/useUnits';
import {formatListDate} from 'src/lib/displayDate';
import type {DetailTransactionFragment} from '@transactions/graphql';

/**
 * Where a foreign transaction's US dollar value came from.
 *
 * The value is not a rate applied at read time — it is basis drawn from
 * specific conversions and stamped here permanently. Showing the draws makes
 * that visible: which withdrawal funded this purchase, and how much of its
 * cost this transaction took. It is also how you notice something odd, like a
 * dinner drawing across two withdrawals at different rates, or a refund
 * handing units back.
 *
 * Renders nothing for home-currency transactions, which have no draws.
 */
export const BasisSection = ({
  transaction,
}: {
  transaction: DetailTransactionFragment;
}) => {
  const formatMoney = useMoneyFormatter();
  const currencyLabel = useCurrencyLabel();
  const draws = transaction.basisDraws;

  if (draws.length === 0 && !transaction.needsBasis) return null;

  return (
    <Section header="US dollar value">
      {transaction.needsBasis && (
        <p className="text-muted-foreground mb-2 text-sm">
          {draws.length === 0
            ? `No value yet — nothing has been converted into ${currencyLabel(transaction.currency)} for this to draw from. Record the conversion that funded it, then assign basis from the inbox.`
            : // Partly funded: the draws below are real and permanent, and are
              // what has to be unwound if this needs reassigning.
              `Partly valued — the draws below are recorded, but there was not enough converted ${currencyLabel(transaction.currency)} to cover all of it. Record another conversion, then assign basis from the inbox.`}
        </p>
      )}
      <div className="flex flex-col gap-2">
        {draws.map((draw, index) => (
          <div
            key={`${draw.lotTransactionId}-${index}`}
            className="flex items-baseline justify-between gap-3 text-sm"
          >
            <span className="min-w-0">
              <Link
                to={`/transactions/${draw.lotTransactionId}`}
                className="hover:underline"
              >
                {draw.lotTransaction?.description || 'Conversion'}
              </Link>
              <span className="text-muted-foreground ml-2 text-xs">
                {draw.lotTransaction && formatListDate(draw.lotTransaction.date)}
                {' · '}
                {/* Negative units are a return: a refund handing them back. */}
                {draw.units < 0 ? 'returned ' : ''}
                {/* The draw's own currency: a conversion is stored as USD
                    but draws yen, so the transaction's currency would render
                    ¥10,000 as $10,000. */}
                {formatMoney(
                  Money.fromInt(Math.abs(draw.units), draw.currency),
                )}
              </span>
            </span>
            <span className="tabular-nums">
              {Money.toDecimalString(Money.fromInt(-draw.homeAmount, 'USD'))}
            </span>
          </div>
        ))}
        {draws.length > 1 && (
          <div className="border-border flex items-baseline justify-between gap-3 border-t pt-2 text-sm font-medium">
            <span>Drawn from {draws.length} conversions</span>
            <span className="tabular-nums">
              {/* The draws themselves, not homeNetAmount — a transfer carries
                  basis while its own value is deliberately 0, so that would
                  total the rows above as $0.00. */}
              {Money.toDecimalString(
                Money.fromInt(
                  -draws.reduce((sum, draw) => sum + draw.homeAmount, 0),
                  'USD',
                ),
              )}
            </span>
          </div>
        )}
      </div>
    </Section>
  );
};
